/**
 * ProgressOverlay Component
 * Shows a modal overlay with progress bar, percentage, time estimate, and stage description
 * during page load/save operations. Blocks navigation while active.
 *
 * Backward compatible: when `progress` prop is not provided, shows simple spinner
 * (same behavior as NavigationBlockOverlay).
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { ProgressOverlayData } from '../services/types';

interface ProgressOverlayProps {
  isBlocked: boolean;
  message: string;
  progress?: ProgressOverlayData | null;
}

function formatTimeRemaining(ms: number | null): string {
  if (ms === null) {
    return 'Calculating...';
  }

  const seconds = Math.ceil(ms / 1000);

  if (seconds <= 0) {
    return 'Almost done...';
  }

  if (seconds === 1) {
    return 'About 1 second remaining';
  }

  if (seconds < 60) {
    return `About ${seconds} seconds remaining`;
  }

  const minutes = Math.ceil(seconds / 60);
  if (minutes === 1) {
    return 'About 1 minute remaining';
  }

  return `About ${minutes} minutes remaining`;
}

export const ProgressOverlay: React.FC<ProgressOverlayProps> = ({
  isBlocked,
  message,
  progress,
}) => {
  if (!isBlocked) return null;

  // Simple spinner mode (backward compatible with NavigationBlockOverlay)
  if (!progress) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)',
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '400px',
          }}
        >
          <Loader2 size={24} className="text-green-600 animate-spin" style={{ flexShrink: 0 }} />
          <span style={{ color: '#374151', fontWeight: 500, fontSize: '14px' }}>{message}</span>
        </div>
      </div>
    );
  }

  // Progress tracking mode
  const { percentage, currentStepLabel, estimatedTimeRemaining, title } = progress;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          padding: '24px 32px',
          width: '100%',
          maxWidth: '420px',
          margin: '0 16px',
        }}
      >
        {/* Title */}
        {title && (
          <h3
            style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827',
              textAlign: 'center',
            }}
          >
            {title}
          </h3>
        )}

        {/* Progress bar container */}
        <div
          style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#E5E7EB',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '12px',
          }}
        >
          {/* Progress bar fill */}
          <div
            style={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: '#16A34A', // green-600
              borderRadius: '4px',
              transition: 'width 0.3s ease-out',
            }}
          />
        </div>

        {/* Percentage and time estimate row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}
        >
          <span
            style={{
              fontSize: '18px',
              fontWeight: 600,
              color: '#16A34A', // green-600
            }}
          >
            {percentage}%
          </span>
          <span
            style={{
              fontSize: '13px',
              color: '#6B7280', // gray-500
            }}
          >
            {formatTimeRemaining(estimatedTimeRemaining)}
          </span>
        </div>

        {/* Current step description */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Loader2
            size={16}
            className="text-green-600 animate-spin"
            style={{ flexShrink: 0 }}
          />
          <span
            style={{
              fontSize: '14px',
              color: '#374151', // gray-700
            }}
          >
            {currentStepLabel}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProgressOverlay;
